import React, { useState, useEffect, useMemo } from 'react';
import Plot from 'react-plotly.js';

const getColorForIndex = (index) => {
    const colors = ['#E41A1C', '#FF7F00', '#FDB863', '#B2DF8A', '#33A02C', '#1F78B4', '#984EA3', '#BE5A7D', '#FB8072', '#E78AC3'];
    return colors[index % colors.length];
};

const GenericGraph = ({ 
    data, 
    title, 
    xAxisTitle, 
    yAxisTitle, 
    xKey, 
    yKey, 
    nameKey, 
    mode = 'lines', 
    type = 'scatter', 
    showLegend = true,
    showIntersection = false,
    initialIntersectY = -1.0
}) => {
    const [intersectY, setIntersectY] = useState(initialIntersectY);
    const [intersectionPoints, setIntersectionPoints] = useState([]);

    // CSV Download Function - NEW
    const downloadCSV = () => {
        let csvContent = '';
        
        // Check if any data has mid_index (CV data with sweeps)
        const hasMidIndex = data.some(item => item.mid_index !== undefined);
        
        // Create headers
        const headers = hasMidIndex 
            ? ['Material', 'Part', xAxisTitle, yAxisTitle]
            : ['Material', xAxisTitle, yAxisTitle];
        csvContent += headers.join(',') + '\n';
        
        // Add data rows
        data.forEach((dataItem) => {
            const x = dataItem[xKey];
            const y = dataItem[yKey];
            const name = dataItem[nameKey] || 'Series';
            
            if (dataItem.mid_index !== undefined) {
                const firstPartName = dataItem.firstPartName || "Forward Sweep";
                const secondPartName = dataItem.secondPartName || "Reverse Sweep";
                
                // First part
                for (let i = 0; i <= dataItem.mid_index; i++) {
                    csvContent += `"${name}","${firstPartName}",${x[i]},${y[i]}\n`;
                }
                
                // Second part
                for (let i = dataItem.mid_index; i < x.length; i++) {
                    csvContent += `"${name}","${secondPartName}",${x[i]},${y[i]}\n`;
                }
            } else {
                // No mid_index, add all data without Part column
                for (let i = 0; i < x.length; i++) {
                    csvContent += `"${name}",${x[i]},${y[i]}\n`;
                }
            }
        });
        
        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${title.toLowerCase().replace(/\s/g, '_')}_data.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Calculate annotations for arrows
    const annotations = useMemo(() => {
        const annotationsList = [];
        
        data.forEach((dataItem, index) => {
            if (dataItem.mid_index !== undefined) {
                // Calculate midpoint for first part (0 to mid_index)
                const firstMidIdx = Math.floor(dataItem.mid_index / 2);
                annotationsList.push({
                    x: dataItem[xKey][firstMidIdx],
                    y: dataItem[yKey][firstMidIdx],
                    xref: 'x',
                    yref: 'y',
                    text: '>',
                    showarrow: false,
                    font: {
                        size: 32,
                        color: 'black'
                    }
                });

                // Calculate midpoint for second part (mid_index to end)
                const secondPartLength = dataItem[xKey].length - dataItem.mid_index;
                const secondMidIdx = dataItem.mid_index + Math.floor(secondPartLength / 2);
                annotationsList.push({
                    x: dataItem[xKey][secondMidIdx],
                    y: dataItem[yKey][secondMidIdx],
                    xref: 'x',
                    yref: 'y',
                    text: '<',
                    showarrow: false,
                    font: {
                        size: 32,
                        color: 'black'
                    }
                });
            }
        });

        return annotationsList;
    }, [data, xKey, yKey]);

    // Process base plot data once, handling mid_index and custom part names if present
    const basePlotData = useMemo(() => {
        return data.flatMap((dataItem, index) => {
            const x = dataItem[xKey];
            const y = dataItem[yKey];
            const name = dataItem[nameKey] || `Series ${index + 1}`;
            
            // If mid_index is present, split the data into two series
            if (dataItem.mid_index !== undefined) {
                // Get custom part names or use defaults
                const firstPartName = dataItem.firstPartName || "Forward Sweep";
                const secondPartName = dataItem.secondPartName || "Reverse Sweep";

                const firstHalf = {
                    x: x.slice(0, dataItem.mid_index + 1),
                    y: y.slice(0, dataItem.mid_index + 1),
                    type: type,
                    mode: mode,
                    name: `${name} (${firstPartName})`,
                    line: { color: getColorForIndex(index * 2), width: 4 },
                    hovertemplate: `${xAxisTitle}: %{x:.4g}<br>${yAxisTitle}: %{y:.4g}<extra></extra>`
                };

                const secondHalf = {
                    x: x.slice(dataItem.mid_index),
                    y: y.slice(dataItem.mid_index),
                    type: type,
                    mode: mode,
                    name: `${name} (${secondPartName})`,
                    line: { color: getColorForIndex(index * 2 + 1), width: 4 },
                    hovertemplate: `${xAxisTitle}: %{x:.4g}<br>${yAxisTitle}: %{y:.4g}<extra></extra>`
                };

                return [firstHalf, secondHalf];
            }

            // If no mid_index, return single series as before
            return [{
                x: x,
                y: y,
                type: type,
                mode: mode,
                name: name,
                line: { color: getColorForIndex(index), width: 4 },
                hovertemplate: `${xAxisTitle}: %{x:.4g}<br>${yAxisTitle}: %{y:.4g}<extra></extra>`
            }];
        });
    }, [data, xKey, yKey, nameKey, mode, type, xAxisTitle, yAxisTitle]);

    // Calculate intersection points and update state
    useEffect(() => {
        if (!showIntersection) {
            setIntersectionPoints([]);
            return;
        }

        const newIntersections = [];
        basePlotData.forEach((series) => {
            const xValues = series.x;
            const yValues = series.y;
            const seriesName = series.name;
            const seriesColor = series.line.color;
            const seriesIntersections = [];

            for (let i = 1; i < yValues.length; i++) {
                if ((yValues[i-1] - intersectY) * (yValues[i] - intersectY) <= 0) {
                    const x = xValues[i-1] + 
                        (xValues[i] - xValues[i-1]) * 
                        (Math.abs(yValues[i-1] - intersectY) / 
                         Math.abs(yValues[i] - yValues[i-1]));
                    
                    seriesIntersections.push(x.toString());
                }
            }

            if (seriesIntersections.length > 0) {
                newIntersections.push({
                    name: seriesName,
                    points: seriesIntersections,
                    color: seriesColor
                });
            }
        });

        setIntersectionPoints(newIntersections);
    }, [basePlotData, intersectY, showIntersection]);

    // Create final plot data including intersections
    const plotData = useMemo(() => {
        if (!showIntersection) return basePlotData;

        const xRange = [
            Math.min(...basePlotData.flatMap(series => series.x)),
            Math.max(...basePlotData.flatMap(series => series.x))
        ];

        // Add intersection line
        const intersectionLine = {
            x: xRange,
            y: [intersectY, intersectY],
            mode: 'lines',
            line: { 
                color: 'rgba(255, 0, 0, 0.5)',
                width: 1,
                dash: 'dot'
            },
            name: 'Intersection Line',
            showlegend: false,
            hoverinfo: 'skip'
        };

        // Add intersection markers
        const intersectionMarkers = intersectionPoints.flatMap(series => 
            series.points.map(point => ({
                x: [parseFloat(point)],
                y: [intersectY],
                mode: 'markers',
                type: 'scatter',
                name: `${series.name} intersection`,
                marker: { 
                    symbol: 'x',
                    size: 10,
                    color: series.color
                },
                showlegend: false,
                hoverinfo: 'skip'
            }))
        );

        return [...basePlotData, intersectionLine, ...intersectionMarkers];
    }, [basePlotData, intersectY, showIntersection, intersectionPoints]);

    const layout = {
        title: { text: title, font: { size: 25, weight: 'bold', color: '#333' } },
        // annotations: annotations,
        xaxis: { 
            title: {
                text: xAxisTitle,
                font: {
                    size: 20
                },
                standoff: 1000
            },
            tickformat: '.2f', 
            gridcolor: '#eee', 
            zeroline: false,
            tickfont: {
                size: 15
            },
            autorange: true,
            nticks: 6,
            exponentformat: 'none',
            separatethousands: false
        },
        yaxis: { 
            title: {
                text: yAxisTitle,
                font: {
                    size: 20
                },
                standoff: 1000
            },
            tickformat: '.2f', 
            gridcolor: '#eee', 
            zeroline: false,
            tickfont: {
                size: 15
            },
            autorange: true,
            nticks: 6,
            exponentformat: 'none',
            separatethousands: false
        },
        legend: showLegend ? {
            x: 1, 
            y: 1, 
            xanchor: 'left', 
            yanchor: 'top',
            bgcolor: 'rgba(255,255,255,0.6)', 
            bordercolor: '#ccc', 
            borderwidth: 1,
            font: { size: 10 },
            itemsizing: 'constant'
        } : {},
        showlegend: showLegend,
        hovermode: 'closest',
        margin: { l: 100, r: 50, t: 50, b: 100 },
        showline: true, 
        mirror: true,
        autosize: true,
        plot_bgcolor: '#fff',
        paper_bgcolor: '#fff',
    };

    const config = {
        responsive: true,
        displayModeBar: 'hover',
        modeBarButtonsToAdd: ['zoom2d', 'pan2d', 'select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d'],
        displaylogo: false,
        doubleClick: 'reset',
        toImageButtonOptions: {
            format: 'png',
            filename: `${title.toLowerCase().replace(/\s/g, '_')}_export`,
            height: 600,
            width: 800,
            scale: 10
        }
    };

    const formatIntersectionPoint = (point) => {
        const val = parseFloat(point);
        return `${val.toPrecision(2)} V`;
    };

    if (!data?.length) {
        return (
            <div className="w-full h-full flex items-center justify-center text-gray-500 text-lg">
                No data available.
            </div>
        );
    }

    return (
        <div className="w-full h-full relative">
            {/* CSV Download Button - NEW */}
            <button
                onClick={downloadCSV}
                className="absolute top-4 left-4 z-20 bg-red-800 hover:bg-blue-700 text-white px-2 py-1 rounded shadow-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                title="Download data as CSV"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                CSV
            </button>

            {showIntersection && (
                <div className="absolute bottom-4 left-4 bg-white/60 border border-gray-200/50 rounded-md p-3 shadow-sm z-10">
                    <div className="mb-2 flex items-center text-sm">
                        <span className="font-medium mr-2 text-sm">Current (<span className="font-serif text-sm">I</span>) = </span>
                        <input
                            type="number"
                            value={intersectY}
                            onChange={(e) => setIntersectY(parseFloat(e.target.value))}
                            step="0.1"
                            className="border rounded px-2 py-0.5 w-14 text-sm"
                        />
                        <span className="ml-1 text-sm">mA/cm²</span>
                    </div>
                    <div className="text-sm space-y-1 overflow-y-auto max-h-[200px]">
                        {intersectionPoints.map((series, idx) => (
                            <div key={idx} className="flex items-center space-x-2 mb-2">
                                <span className="font-medium min-w-[1rem]">{series.name}</span>
                                <div className="w-4 h-0.5 flex-shrink-0" style={{ backgroundColor: series.color }}></div>
                                <span className="break-all">
                                    {series.points.length === 1 
                                        ? formatIntersectionPoint(series.points[0])
                                        : series.points.map(point => formatIntersectionPoint(point)).join(' ')}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            <Plot
                data={plotData}
                layout={layout}
                config={config}
                style={{ width: '100%', height: '80%' }}
                useResizeHandler={true}
            />
        </div>
    );
};

export default GenericGraph;